/**
 * Предупреждение там, где назначают напоминание.
 *
 * Время напоминания задаётся в привычке, в курсе лекарства, в намеченном
 * приёме — и сохраняется молча. А уведомления при этом могут быть не
 * разрешены: система спрашивает об этом один раз, и пока разрешения нет, ни
 * одно напоминание показано не будет. Со стороны это выглядит как поломка:
 * человек выбрал время, ждал — и ничего не пришло.
 *
 * Поэтому предупреждение стоит рядом с самим переключателем, а не в настройках,
 * куда надо догадаться зайти. И разрешение спрашивается здесь же: отправлять
 * человека «в профиль, в раздел напоминаний» ради одного нажатия — значит
 * потерять половину по дороге.
 *
 * Препятствий два, и оба видны отсюда: системное разрешение и выключатель в
 * профиле. Проверять только первое оказалось мало — человек, выключивший
 * напоминания у себя в настройках, ставил время в привычке и снова оставался
 * без предупреждения: та же ловушка этажом глубже.
 *
 * Когда всё разрешено и включено, блока нет вовсе: напоминание о том, что всё
 * в порядке, читают ровно один раз, а потом перестают замечать и его, и
 * предупреждения.
 */

import { BellOff, BellRing } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { updateProfile, useProfile } from '@/entities/profile/model/use-profile';
import { cn } from '@/shared/lib/cn';
import {
  getPermission,
  type NotificationPermission,
  requestPermission,
} from '@/shared/notifications/local-notifications';
import { Button, toast } from '@/shared/ui';

interface ReminderPermissionProps {
  className?: string;
}

export function ReminderPermission({ className }: ReminderPermissionProps) {
  const [permission, setPermission] = useState<NotificationPermission | null>(null);
  const profile = useProfile();

  const refresh = useCallback(() => {
    void getPermission().then(setPermission);
  }, []);

  useEffect(() => {
    refresh();

    // Разрешение могли выдать в системных настройках, не закрывая приложение.
    document.addEventListener('visibilitychange', refresh);
    return () => document.removeEventListener('visibilitychange', refresh);
  }, [refresh]);

  const switchedOff = profile !== undefined && profile !== null && !profile.notificationsEnabled;

  if (permission === null) return null;
  if (permission === 'granted' && !switchedOff) return null;

  const allow = () => {
    void requestPermission().then((result) => {
      setPermission(result);

      if (result === 'granted') toast.success('Напоминания включены');
      if (result === 'denied') {
        toast.error('Уведомления отклонены — включить их можно в настройках телефона');
      }
    });
  };

  const turnOn = () => {
    if (!profile) return;
    void updateProfile(profile.id, { notificationsEnabled: true }).then(() => {
      toast.success('Напоминания включены');
    });
  };

  const wrapper = cn(
    'flex flex-wrap items-center gap-2 rounded-md border px-3 py-2 text-[13px] leading-relaxed',
    permission === 'denied'
      ? 'border-danger/30 bg-danger-soft text-danger'
      : 'border-warning/30 bg-warning-soft text-warning',
    className,
  );

  /*
    Разрешение есть, но напоминания выключены в самом приложении. Это не
    ошибка и не запрет — это решение человека, о котором он мог забыть;
    поэтому тон спокойный, а включение стоит рядом.
  */
  if (permission === 'granted') {
    return (
      <div className={wrapper}>
        <BellRing size={16} className="shrink-0" aria-hidden />
        <span className="min-w-0 flex-1">
          Напоминания выключены в профиле — это не придёт, пока они выключены.
        </span>
        <Button size="sm" variant="secondary" onClick={turnOn}>
          Включить
        </Button>
      </div>
    );
  }

  if (permission === 'unsupported') {
    return (
      <p className={wrapper}>
        <BellOff size={16} className="shrink-0" aria-hidden />
        <span className="min-w-0 flex-1">
          Это устройство не умеет показывать уведомления — напоминание не придёт. Время можно
          оставить: оно видно в карточке.
        </span>
      </p>
    );
  }

  if (permission === 'denied') {
    return (
      <p className={wrapper}>
        <BellOff size={16} className="shrink-0" aria-hidden />
        <span className="min-w-0 flex-1">
          Уведомления запрещены в настройках телефона — напоминание не придёт, пока вы их не
          разрешите там же, в настройках приложения Kairo.
        </span>
      </p>
    );
  }

  return (
    <div className={wrapper}>
      <BellRing size={16} className="shrink-0" aria-hidden />
      <span className="min-w-0 flex-1">
        Напоминание не придёт: уведомления ещё не разрешены.
      </span>
      <Button size="sm" variant="secondary" onClick={allow}>
        Разрешить
      </Button>
    </div>
  );
}
